import type {
  CompanyPartnerEnrollmentStatus,
  CreateOrUpdatePaymentMethodPayPalInput, AddMemberToPartnerInput, VerifyMemberPaypalIdentityInput,
  AddOrUpdateMemberConfigurationInput, SaveCompanyPartnerRelationInput, UpdateCompanyInput,
  SignUpMemberForHdSupplyInput,
  AddressSearchInput,
  CreateOrUpdatePaymentMethodACHInput,
  CreateOrUpdatePaymentMethodPaperChequeInput,
  UpdateCompanyProfileInput,
  PartnerEnrollInput,
  UpdateCompanyPartnerInput,
} from '@optimus/models';
import { axiosInstance } from './apiConfig';

type StringifyTypeDiscriminator<T> = T extends { type: infer Type extends string }
  ? Omit<T, 'type'> & { type: `${Type}` }
  : T;

export type PartnerEnrollRequest = StringifyTypeDiscriminator<PartnerEnrollInput>;

export type UpdateCompanyPartnerRequest =
  Omit<UpdateCompanyPartnerInput, 'enrollmentStatus'> & {
    enrollmentStatus?: `${CompanyPartnerEnrollmentStatus}`;
  };

export const getAllActivePartners = async (membershipType?: string) => {
  const { data } = await axiosInstance.get('/partners', {
    params: { populate: 'config', status: 'active', ...(membershipType && { membershipType }) },
  });
  return data;
};

export const getRecentRebatePartners = async (memberId: string) => {
  const { data } = await axiosInstance.get(`/members/${memberId}/recent-rebate-partners`);
  return data;
};

export const getPaymentMethods = async (memberId: string | undefined) => {
  const { data } = await axiosInstance.get(`/members/${memberId}/payment-methods`);
  return data;
};

export const getLennoxMemberRelation = async (memberId: string, lennoxPartnerId: string) => {
  const { data } = await axiosInstance.get(`/members/${memberId}/partners/${lennoxPartnerId}`);
  return data;
};

export const getHomeDepotMemberRelation = async (memberId: string, hdPartnerId: string) => {
  const { data } = await axiosInstance.get(`/members/${memberId}/partners/${hdPartnerId}`);
  return data;
};

export const getCompanyPartnerRelations = async (companyId: string) => {
  const { data } = await axiosInstance.get(`/company-partner-relations?companyId=${companyId}`);
  return data;
};

export const getCompanyWallet = async (companyId: string) => {
  const { data } = await axiosInstance.get(`/rewards/company/${companyId}/payouts-and-rewards`);
  return data;
};

export const getCompanyRewardsWallet = async (companyId: string) => {
  const { data } = await axiosInstance.get(`/rewards/company/${companyId}/rewards`);
  return data;
};

export const getCompanyPayoutsWallet = async (companyId: string) => {
  const { data } = await axiosInstance.get(`/rewards/company/${companyId}/payouts`);
  return data;
};

export const setPaymentMethod =
  async (
    memberId: string | undefined,
    request:
    CreateOrUpdatePaymentMethodPayPalInput |
    CreateOrUpdatePaymentMethodACHInput |
    CreateOrUpdatePaymentMethodPaperChequeInput,
  ) => {
    const { data } = await axiosInstance.post(`/members/${memberId}/payment-methods`, request);
    return data;
  };

export const setLennoxMemberKey =
  async (
    memberId: string,
    partnerId: string,
    requestBody: AddMemberToPartnerInput,
  ) => {
    const { data } = await axiosInstance.put(`/members/${memberId}/partners/${partnerId}`, requestBody);
    return data;
  };

export const updateMemberToPartner =
  async (
    memberId: string,
    partnerId: string,
    requestBody: AddMemberToPartnerInput,
  ) => {
    const { data } = await axiosInstance.put(`/members/${memberId}/partners/${partnerId}`, requestBody);
    return data;
  };

export const deleteMemberPartnerRelation =
  async (
    memberId: string,
    partnerId: string,
  ) => {
    const { data } = await axiosInstance.delete(`/members/${memberId}/partners/${partnerId}`);
    return data;
  };

export const getSelectedPartnersForMember =
  async (
    memberId: string,
  ) => {
    const { data } = await axiosInstance.get(`/members/${memberId}/partners`);
    return data;
  };

export const getYearlyPaymentsById =
  async (
    memberId: string,
  ) => {
    const { data } = await axiosInstance.get(`/payment-details/year/${memberId}`);
    return data;
  };

export const updateMemberConfig = async (
  request: AddOrUpdateMemberConfigurationInput,
  memberId: string,
  configurationId: string,
) => {
  const { data } = await axiosInstance.put(
    `/members/${memberId}/configurations/${configurationId}`,
    request,
  );
  return data;
};

export const getMemberConfig = async (memberId: string) => {
  const { data } = await axiosInstance.get(`/members/${memberId}/configurations`);
  return data;
};

export const createMemberConfig = async (
  request: AddOrUpdateMemberConfigurationInput,
  memberId: string,
) => {
  const { data } = await axiosInstance.post(`/members/${memberId}/configurations`, request);
  return data;
};

export const updateCompanyPartnerRelations = async (
  request: SaveCompanyPartnerRelationInput,
) => {
  const { data } = await axiosInstance.post('/company-partner-relations', request);
  return data;
};

export const enrollCompanyToPartner = async (
  companyId: string,
  partnerId: string,
  request: PartnerEnrollRequest,
) => {
  const { data } = await axiosInstance.post(
    `/companies/${companyId}/partners/${partnerId}/enroll`,
    request,
  );
  return data;
};

export const updateCompanyPartner = async (
  companyId: string,
  partnerId: string,
  request: UpdateCompanyPartnerRequest,
) => {
  const { data } = await axiosInstance.post(
    `/companies/${companyId}/partners/${partnerId}`,
    request,
  );
  return data;
};

export const unlinkCompanyFromPartner = async (
  companyId: string,
  partnerId: string,
) => {
  const { data } = await axiosInstance.post(
    `/companies/${companyId}/partners/${partnerId}/unlink`,
  );
  return data;
};

export const updateCompanyInformation = async (
  request: UpdateCompanyInput,
  companyId: string,
) => {
  const { data } = await axiosInstance.put(`/companies/${companyId}`, request);
  return data;
};

export const signUpMemberForHdSupply = async (
  request: SignUpMemberForHdSupplyInput,
  memberId: string,
) => {
  const { data } = await axiosInstance.post(`/members/${memberId}/hd-supply-signup`, request);
  return data;
};

export const getHDSupplyMemberRelation = async (memberId: string, hdSupplyPartnerId: string) => {
  const { data } = await axiosInstance.get(`/members/${memberId}/partners/${hdSupplyPartnerId}`);
  return data;
};

export const verifyAndUpdatePaypalEmail = async (
  memberId: string,
  request: VerifyMemberPaypalIdentityInput,
) => {
  const { data } = await axiosInstance.post(`/members/${memberId}/paypal-verification`, request);
  return data;
};

export const unlinkPaymentMethod = async (paymentMethodId: string) => {
  const { data } = await axiosInstance.get(`/payment-methods/${paymentMethodId}/unlink`);
  return data;
};

export const searchAddressesByQueryText = async (
  request: AddressSearchInput,
) => {
  const { data } = await axiosInstance.post(
    '/member-address-search',
    request,
  );
  return data;
};

export const activatePaymentMethod =
  async (
    paymentMethodId: string | undefined,
  ) => {
    const { data } = await axiosInstance.post(`/payment-methods/${paymentMethodId}/activate`);
    return data;
  };

export const updateCompanyProfile = async (
  request: UpdateCompanyProfileInput,
  companyId: string,
) => {
  const { data } = await axiosInstance.post(
    `/companies/${companyId}/profile`,
    request,
  );
  return data;
};

export const getPartnerProfileConfig = async (partnerId: string) => {
  const { data } = await axiosInstance.get(`/partner-profile/${partnerId}`);
  return data;
};

/**
 * The category taxonomy is a small fixed list - nine keys, at most one row per locale - so
 * one page always covers it and no pagination fallback is needed. The endpoint's own default
 * page size is 20, which sits close to that ceiling once Spanish rows exist, so the request
 * asks for an explicit size with real headroom instead.
 */
const CATEGORY_PAGE_SIZE = 50;

// No membershipType: the category chips label the cumulative partner list, so they have to
// cover every tier's categories (SN-996).
export const getAllCategories = async () => {
  const { data } = await axiosInstance.get('/categories', {
    params: { take: CATEGORY_PAGE_SIZE },
  });
  return data;
};

export const createCompanyPartnerOptin = async (
  companyId: string,
  partnerId: string,
) => {
  const { data } = await axiosInstance.post(
    `/companies/${companyId}/partner-optins`,
    { partnerId },
  );
  return data;
};

export const requestContactSales = async (
  companyId: string,
  partnerId: string,
) => {
  await axiosInstance.post(
    `/companies/${companyId}/partners/${partnerId}/contact-sales`,
  );
};

/**
 * Contact sales about a membership rather than a partner. Used by locked content
 * that requires Advanced or Alliance, neither of which is sold in app, so there is
 * no partner to scope the request to (SN-996). Sales get the same profile details,
 * with no partner lines in the email.
 */
export const requestCompanyContactSales = async (companyId: string) => {
  await axiosInstance.post(`/companies/${companyId}/contact-sales`);
};

export const getCompanyPartnerOptin = async (companyId: string, partnerId: string) => {
  const { data } = await axiosInstance.get(
    `/companies/${companyId}/partner-optins?partnerId=${partnerId}&optinType=Optin`,
  );
  return data;
};

export const getPartnerCategories = async (partnerId: string) => {
  const { data } = await axiosInstance.get(`/partners/${partnerId}/categories`);
  return data;
};

export const getPartnersByCategories = async (categoryIds: string[], membershipType?: string) => {
  const { data } = await axiosInstance.get('/partners', {
    params: {
      ...(categoryIds.length > 0 && { categoryIds: categoryIds.join(',') }),
      status: 'active',
      ...(membershipType && { membershipType }),
    },
  });
  return data;
};
